<template>
    <div class="admin-layout">
        <aside :class="['sidebar', { 'sidebar-collapsed': sidebarCollapsed }]">
            <div class="sidebar-header">
                <div class="logo">
                    <div class="logo-icon">
                        <img v-if="systemLogo" :src="systemLogo" alt="Logo" class="logo-image">
                        <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                            <rect x="6" y="6" width="12" height="12" rx="1" fill="currentColor" opacity="0.3"/>
                            <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" opacity="0.5"/>
                        </svg>
                    </div>
                    <span v-if="!sidebarCollapsed" class="logo-text">{{ systemName }}</span>
                </div>
            </div>

            <nav class="sidebar-nav">
                <a v-if="canShowDashboard" href="/admin/dashboard" class="nav-item" :class="{ 'active': currentRouteGroup === 'dashboard' }">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 2.5H8.33333V8.33333H2.5V2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        <path d="M11.6667 2.5H17.5V8.33333H11.6667V2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        <path d="M2.5 11.6667H8.33333V17.5H2.5V11.6667Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        <path d="M11.6667 11.6667H17.5V17.5H11.6667V11.6667Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                    </svg>
                    <span v-if="!sidebarCollapsed">{{ adminT('dashboard') }}</span>
                </a>

                <div v-if="permissionsLoaded && canShowUsersGroup" class="nav-group">
                    <button
                        type="button"
                        class="nav-item nav-parent"
                        :class="{ 'active': currentRouteGroup === 'users' }"
                        @click="toggleUsersMenu"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M2.5 18.3333C2.5 14.7534 5.35355 11.9 8.93333 11.9H11.0667C14.6464 11.9 17.5 14.7534 17.5 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <span v-if="!sidebarCollapsed" class="nav-parent-text">{{ adminT('users_management') }}</span>
                        <svg v-if="!sidebarCollapsed" class="nav-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path :d="usersMenuOpen ? 'M5 12.5L10 7.5L15 12.5' : 'M5 7.5L10 12.5L15 7.5'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div v-if="!sidebarCollapsed && usersMenuOpen" class="nav-submenu">
                        <a v-if="canShowUsersLink" href="/admin/users" class="nav-subitem" :class="{ 'active': currentRoute === 'users' }">
                            {{ adminT('user_list') }}
                        </a>
                        <a v-if="canShowRolesLink" href="/admin/roles" class="nav-subitem" :class="{ 'active': currentRoute === 'roles' }">
                            {{ adminT('roles') }}
                        </a>
                        <a v-if="canShowPermissionsLink" href="/admin/permissions" class="nav-subitem" :class="{ 'active': currentRoute === 'permissions' }">
                            {{ adminT('permissions') }}
                        </a>
                    </div>
                </div>

                <div v-if="permissionsLoaded && canShowRegistrationGroup" class="nav-group">
                    <button
                        type="button"
                        class="nav-item nav-parent"
                        :class="{ 'active': currentRouteGroup === 'registration' }"
                        @click="toggleRegistrationMenu"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M6.66667 1.66667V4.16667M13.3333 1.66667V4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <path d="M2.5 7.5H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <path d="M4.16667 3.33333H15.8333C16.7538 3.33333 17.5 4.07953 17.5 5V15.8333C17.5 16.7538 16.7538 17.5 15.8333 17.5H4.16667C3.24619 17.5 2.5 16.7538 2.5 15.8333V5C2.5 4.07953 3.24619 3.33333 4.16667 3.33333Z" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M6.25 10H13.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <path d="M6.25 13.3333H11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <span v-if="!sidebarCollapsed" class="nav-parent-text">{{ adminT('registration') }}</span>
                        <svg v-if="!sidebarCollapsed" class="nav-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path :d="registrationMenuOpen ? 'M5 12.5L10 7.5L15 12.5' : 'M5 7.5L10 12.5L15 7.5'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div v-if="!sidebarCollapsed && registrationMenuOpen" class="nav-submenu">
                        <a v-if="canShowRegistrationQueueLink" href="/admin/registration-queue" class="nav-subitem" :class="{ 'active': currentRoute === 'registration-queue' }">
                            {{ adminT('registration_queue') }}
                        </a>
                    </div>
                </div>

                <div v-if="permissionsLoaded && canShowMembershipGroup" class="nav-group">
                    <button
                        type="button"
                        class="nav-item nav-parent"
                        :class="{ 'active': currentRouteGroup === 'membership' }"
                        @click="toggleMembershipMenu"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M2.5 18.3333C2.5 14.7534 5.35355 11.9 8.93333 11.9H11.0667C14.6464 11.9 17.5 14.7534 17.5 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <span v-if="!sidebarCollapsed" class="nav-parent-text">{{ adminT('membership') }}</span>
                        <svg v-if="!sidebarCollapsed" class="nav-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path :d="membershipMenuOpen ? 'M5 12.5L10 7.5L15 12.5' : 'M5 7.5L10 12.5L15 7.5'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div v-if="!sidebarCollapsed && membershipMenuOpen" class="nav-submenu">
                        <a v-if="canShowMembershipListLink" href="/admin/customers" class="nav-subitem" :class="{ 'active': currentRoute === 'customers' }">
                            {{ adminT('membership_list') }}
                        </a>
                    </div>
                </div>

                <div v-if="permissionsLoaded && canShowPropertyGroup" class="nav-group">
                    <button
                        type="button"
                        class="nav-item nav-parent"
                        :class="{ 'active': currentRouteGroup === 'property' }"
                        @click="togglePropertyMenu"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M3.33333 8.33333L10 2.5L16.6667 8.33333V16.6667H3.33333V8.33333Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                            <path d="M7.5 16.6667V11.6667H12.5V16.6667" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        </svg>
                        <span v-if="!sidebarCollapsed" class="nav-parent-text">{{ adminT('property') }}</span>
                        <svg v-if="!sidebarCollapsed" class="nav-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path :d="propertyMenuOpen ? 'M5 12.5L10 7.5L15 12.5' : 'M5 7.5L10 12.5L15 7.5'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div v-if="!sidebarCollapsed && propertyMenuOpen" class="nav-submenu">
                        <a v-if="canShowPropertyListLink" href="/admin/properties" class="nav-subitem" :class="{ 'active': currentRoute === 'properties' }">
                            {{ adminT('property_list') }}
                        </a>
                        <a v-if="canShowPackageChargesLink" href="/admin/package-charges" class="nav-subitem" :class="{ 'active': currentRoute === 'package-charges' }">
                            {{ adminT('package_charges') }}
                        </a>
                    </div>
                </div>

                <div v-if="permissionsLoaded && canShowSeekerGroup" class="nav-group">
                    <button
                        type="button"
                        class="nav-item nav-parent"
                        :class="{ 'active': currentRouteGroup === 'seeker' }"
                        @click="toggleSeekerMenu"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M2.5 18.3333C2.5 14.7534 5.35355 11.9 8.93333 11.9H11.0667C14.6464 11.9 17.5 14.7534 17.5 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <path d="M6.66667 7.5H13.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <span v-if="!sidebarCollapsed" class="nav-parent-text">{{ adminT('rent_seeker') }}</span>
                        <svg v-if="!sidebarCollapsed" class="nav-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path :d="seekerMenuOpen ? 'M5 12.5L10 7.5L15 12.5' : 'M5 7.5L10 12.5L15 7.5'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div v-if="!sidebarCollapsed && seekerMenuOpen" class="nav-submenu">
                        <a v-if="canShowActivePackageLink" href="/admin/rent-seeker/active-package" class="nav-subitem" :class="{ 'active': currentRoute === 'active-package' }">
                            {{ adminT('active_package') }}
                        </a>
                        <a v-if="canShowShortlistLink" href="/admin/rent-seeker/shortlist" class="nav-subitem" :class="{ 'active': currentRoute === 'shortlist' }">
                            {{ adminT('shortlist') }}
                        </a>
                    </div>
                </div>

                <a v-if="permissionsLoaded && canShowBilling" href="/admin/billing" class="nav-item" :class="{ 'active': currentRouteGroup === 'billing' }">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M5 1.66667H13.3333L16.6667 5V18.3333H5V1.66667Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        <path d="M13.3333 1.66667V5H16.6667" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        <path d="M7.5 8.33333H14.1667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <path d="M7.5 11.6667H14.1667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <path d="M7.5 15H11.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                    <span v-if="!sidebarCollapsed">{{ adminT('billing') }}</span>
                </a>

                <a v-if="permissionsLoaded && canShowSettings" href="/admin/settings" class="nav-item" :class="{ 'active': currentRouteGroup === 'settings' }">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M8.33333 2.5H11.6667L12.5 4.16667L14.1667 5L15.8333 4.16667L17.5 6.66667L16.6667 8.33333L17.5 10L16.6667 11.6667L17.5 13.3333L15.8333 15.8333L14.1667 15L12.5 15.8333L11.6667 17.5H8.33333L7.5 15.8333L5.83333 15L4.16667 15.8333L2.5 13.3333L3.33333 11.6667L2.5 10L3.33333 8.33333L2.5 6.66667L4.16667 4.16667L5.83333 5L7.5 4.16667L8.33333 2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <circle cx="10" cy="10" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                    <span v-if="!sidebarCollapsed">{{ adminT('settings') }}</span>
                </a>
            </nav>

            <div class="sidebar-footer">
                <button @click="toggleSidebar" class="sidebar-toggle">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path v-if="sidebarCollapsed" d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path v-else d="M12.5 5L7.5 10L12.5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </aside>

        <div class="main-content">
            <header class="top-header">
                <div class="header-left">
                    <h1 class="page-title">{{ pageTitle }}</h1>
                </div>
                <div class="header-right">
                    
                    <div class="lang-pill" role="group" :aria-label="adminT('language')">
                        <button type="button" class="lang-pill-btn" :class="{ active: lang === 'en' }" @click="setLang('en')">English</button>
                        <button type="button" class="lang-pill-btn" :class="{ active: lang === 'bn' }" @click="setLang('bn')">বাংলা</button>
                    </div>
                    <button type="button" class="theme-btn" @click="toggleTheme" :aria-label="adminT(theme === 'dark' ? 'switch_to_light_mode' : 'switch_to_dark_mode')">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M17.5 12.1C16.7 12.4 15.8 12.6 14.9 12.6C11 12.6 7.9 9.5 7.9 5.6C7.9 4.7 8.1 3.8 8.4 3C5.3 4.1 3.1 7.1 3.1 10.6C3.1 15 6.6 18.5 11 18.5C14.4 18.5 17.4 16.3 18.5 13.2C18.2 12.8 17.9 12.4 17.5 12.1Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <div class="user-pill">
                        <div class="user-pill-icon" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M2.5 18.3333C2.5 14.7534 5.35355 11.9 8.93333 11.9H11.0667C14.6464 11.9 17.5 14.7534 17.5 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <div class="user-pill-name">{{ userName }}</div>
                    </div>
                    <button @click="logout" class="logout-btn logout-danger" aria-label="Logout">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M7.5 17.5H4.16667C3.24619 17.5 2.5 16.7538 2.5 15.8333V4.16667C2.5 3.24619 3.24619 2.5 4.16667 2.5H7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M13.3333 14.1667L17.5 10L13.3333 5.83333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 10H7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </header>

            <main class="content-area">
                <slot></slot>
            </main>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, provide } from 'vue';
import axios from 'axios';

export default {
    name: 'AdminLayout',
    setup() {
        const sidebarCollapsed = ref(false);
        const userName = ref(document.querySelector('meta[name="user-name"]')?.getAttribute('content') || 'Admin');
        const systemName = ref(document.querySelector('meta[name="system-name"]')?.getAttribute('content') || 'My Housing BD');
        const systemLogo = ref(document.querySelector('meta[name="system-logo"]')?.getAttribute('content') || '');
        const currentRoute = ref('users');
        const currentRouteGroup = ref('users');
        const usersMenuOpen = ref(true);
        const registrationMenuOpen = ref(false);
        const membershipMenuOpen = ref(false);
        const propertyMenuOpen = ref(false);
        const seekerMenuOpen = ref(false);
        const permissionsLoaded = ref(false);
        const userPermissions = ref([]);
        const lang = ref(localStorage.getItem('admin_lang') || 'en');
        const theme = ref(localStorage.getItem('admin_theme') || 'light');

        const translations = {
            en: {
                dashboard: 'Dashboard',
                welcome_admin: 'Welcome Admin',
                logout: 'Logout',
                open_page: 'Open Page',
                registration: 'Registration',
                registration_queue: 'Registration Queue',
                registration_queue_subtitle: 'Approve or review new membership registrations',
                billing: 'Billing',
                settings: 'Settings',
                settings_subtitle: 'Manage system configuration',
                language: 'Language',
                language_subtitle: 'Change admin panel language (ENG / বাংলা)',
                switch_to_light_mode: 'Switch to light mode',
                switch_to_dark_mode: 'Switch to dark mode',
                configure: 'Configure',
                save: 'Save',
                reset: 'Reset',
                reload: 'Reload',
                save_all: 'Save All',
                settings_saved: 'Settings saved.',
                loading: 'Loading...',
                no_settings_section_found: 'No settings section found.',
                customer_payment: 'Customer Payment',
                customer_payment_subtitle: 'Approve or review customer payments',
                members: 'Members',
                members_subtitle: 'Manage member list, status and details',
                pending: 'Pending',
                processed: 'Processed',
                confirmed: 'Confirmed',
                all: 'All',
                refresh: 'Refresh',
                per_page: 'Per page',
                page: 'Page',
                last_updated: 'Last updated',
                confirm_payment: 'Confirm Payment',
                confirm_payment_hint: 'Please verify the payment information before confirming.',
                search: 'Search',
                total: 'Total',
                date: 'Date',
                member_type: 'Member Type',
                corporate: 'Corporate',
                personal: 'Personal',
                category: 'Category',
                owner: 'Owner',
                seeker: 'Seeker',
                customer_name: 'Customer Name',
                customer: 'Customer',
                organization_name: 'Organization Name',
                mobile: 'Mobile',
                email: 'Email',
                district: 'District',
                status: 'Status',
                actions: 'Actions',
                accept: 'Accept',
                confirm: 'Confirm',
                add_member: 'Add Member',
                edit_member: 'Edit Member',
                member_details: 'Member Details',
                view: 'View',
                edit: 'Edit',
                delete: 'Delete',
                activate: 'Activate',
                deactivate: 'Deactivate',
                close: 'Close',
                cancel: 'Cancel',
                previous: 'Previous',
                next: 'Next',
                first: 'First',
                last: 'Last',
                from_date: 'From',
                to_date: 'To',
                clear_filters: 'Clear',
                showing_entries: 'Showing {from} to {to} of {total} entries',
                filtered: '(Filtered: {count})',
                confirm_delete: 'Are you sure you want to delete?',
                failed: 'Failed',
                failed_to_load: 'Failed to load',
                failed_to_load_settings: 'Failed to load settings.',
                failed_to_save_settings: 'Failed to save settings.',
                validation_failed: 'Validation failed.',
                charge_calculation_failed: 'Charge calculation failed',
                fill_required_for_charge_calc: 'Please provide Property Type, Unit, Unit Size and Rent.',
                optional: 'Optional',
                password: 'Password',
                select: 'Select',
                residence: 'Residence',
                inside_city: 'Inside City',
                outside_city: 'Outside City',
                nationality: 'Nationality',
                bangladeshi: 'Bangladeshi',
                others: 'Others',
                country: 'Country',
                bill_no: 'Bill No',
                bill_type: 'Bill Type',
                amount: 'Amount',
                payment_date: 'Payment Date',
                payment_method: 'Payment Method',
                trx: 'Transaction',
                no_data: 'No data found',
                success: 'Success',
                users_management: 'Users Management',
                users_management_subtitle: 'Manage all system users',
                user_list: 'User List',
                roles: 'Roles',
                permissions: 'Permissions',
                rent_seeker: 'Rent Seeker',
                active_package: 'Active Package',
                active_package_subtitle: 'Manage rent seeker active plans (CRUD)',
                shortlist: 'Shortlist',
                shortlist_subtitle: 'Manage saved list / short list (CRUD)',
                add_new_user: 'Add New User',
                search_users: 'Search users',
                login_id: 'Login ID',
                user_type: 'User Type',
                mobile_no: 'Mobile No',
                pass_reset: 'Pass Reset',
                last_login: 'Last Login',
                created: 'Created',
                updated: 'Updated',
                no_role: 'No Role',
                view_only: 'View Only',
                create_new_user: 'Create New User',
                edit_user: 'Edit User',
                is_pass_reset_required: 'Is Pass Reset Required',
                enter_new_password: 'Enter new password',
                confirm_password: 'Confirm Password',
                confirm_password_placeholder: 'Confirm password',
                password_leave_blank: 'Leave blank to keep current password',
                last_login_date: 'Last Login Date',
                create_date: 'Create Date',
                update_date: 'Update Date',
                roles_page_subtitle: 'Manage roles and assign permissions',
                create_role: 'Create Role',
                search_roles: 'Search roles',
                slug: 'Slug',
                users: 'Users',
                permissions_count: 'Permissions',
                permissions_page_subtitle: 'Manage permissions (create/edit/delete)',
                create_permission: 'Create Permission',
                search_permissions: 'Search permissions',
                group: 'Group',
                description: 'Description',
                general: 'General',
                auto_generate_if_empty: 'auto-generate if empty',
                eg_users_management: 'e.g. Users Management',
                optional_placeholder: 'Optional',
                pending_confirmed: 'Pending / Confirmed',
                name: 'Name',
                select_visible: 'Select Visible',
                clear: 'Clear',
                no_permissions_found: 'No permissions found.',
                select_at_least_one_permission: 'Please select at least one permission.',
                role_saved_permission_update_failed: 'Role saved, but permission update failed (missing role id).',
                role_saved_success: 'Role saved successfully!',
                failed_to_save_role: 'Failed to save role.',
                role_deleted: 'Role deleted.',
                failed_to_delete_role: 'Failed to delete role.',
                permission_saved_success: 'Permission saved successfully!',
                failed_to_save_permission: 'Failed to save permission.',
                permission_deleted: 'Permission deleted.',
                failed_to_delete_permission: 'Failed to delete permission.',
                publicity_start: 'Publicity Start',
                publicity_end: 'Publicity End',
                available_from: 'Available From',
                publicity_start_date: 'Publicity Start Date',
                publicity_end_date: 'Publicity End Date',
                property_available_from: 'Property Available From',
                floor_type_placeholder: 'Normal / Tiles / Mosaic',
                floor_level_placeholder: 'e.g. 5th',
                info: 'Info',
                location: 'Location',
                media: 'Media',
                bill: 'Bill',
                id: 'ID',
                late_payment: 'Late Payment',
                allowed: 'Allowed',
                due: 'Due',
                fee: 'Fee',
                features: 'Features',
                preferences: 'Preferences',
                rooms_spaces: 'Rooms & Spaces',
                total_room: 'Total Room',
                balconies: 'Balconies',
                dining_room: 'Dining Room',
                dining_room_size: 'Dining Room Size',
                drawing_room: 'Drawing Room',
                drawing_room_size: 'Drawing Room Size',
                attached_dining_drawing: 'Attached Dining & Drawing',
                attached_dining_drawing_size: 'Attached Dining & Drawing Size',
                floor_type: 'Floor Type',
                floor_level: 'Floor Level',
                total_floor_in_building: 'Total Floor in Building',
                kitchen_features: 'Kitchen Features',
                has_kitchen: 'Has Kitchen',
                kitchen_description: 'Kitchen Description',
                gas_connection_type: 'Gas Connection Type',
                bathroom_features: 'Bathroom Features',
                attached_bath: 'Attached Bath',
                common_bath: 'Common Bath',
                high_commode: 'High Commode',
                low_commode: 'Low Commode',
                utility_services: 'Utility & Services',
                electricity: 'Electricity',
                dedicated: 'Dedicated',
                shared: 'Shared',
                water_wasa: 'Water (WASA)',
                water_deep_tubewell: 'Water (Deep Tube-well)',
                lift: 'Lift',
                parking_space: 'Parking Space',
                community_space: 'Community Space',
                generator_backup_power: 'Generator/Backup Power',
                internet_broadband: 'Internet / Broadband',
                security_safety: 'Security & Safety',
                security_guard: 'Security Guard',
                cctv_surveillance: 'CCTV Surveillance',
                intercom_system: 'Intercom System',
                fire_safety_system: 'Fire Safety System',
                furnishing_status: 'Furnishing Status',
                unfurnished: 'Unfurnished',
                furnished: 'Furnished',
                furnished_details: 'Furnished Details',
                residential_preference: 'Residential Preference',
                family: 'Family',
                small_family: 'Small Family',
                bachelor: 'Bachelor',
                student_male: 'Student (Male)',
                student_female: 'Student (Female)',
                service_holder: 'Service Holder',
                any: 'Any',
                attachment_rate_list: 'Attachment Rate List',
                no_active_attachment_rate: 'No active attachment rate found.',
                public_view_status: 'Public View Status',
                visible: 'Visible',
                hidden: 'Hidden',
                bill_id: 'Bill ID',
                bill_status: 'Bill Status',
                plan_id: 'Plan ID',
                plan_start_date: 'Plan Start Date',
                validity_date: 'Validity Date',
                customer_active_plan_id: 'Customer Active Plan ID',
                to_let_notice_id: 'ToLet Notice ID',
                list_type: 'List Type',
                saved: 'Saved',
                short: 'Short',
                make_saved: 'Make Saved',
                make_short: 'Make Short',
                saving: 'Saving...',
                estimated_publish_charge: 'Estimated Publish Charge',
                calculate: 'Calculate',
                calculating: 'Calculating...',
                space: 'Space',
                attachments: 'Attachments',
                set_unit_unit_size_rent_then_calculate: 'Set Unit, Unit Size and Rent then click Calculate.',
                late_payment_allowed: 'Late Payment Allowed',
                payment_due_date: 'Payment Due Date',
                late_fee_type: 'Late Fee Type',
                fixed: 'Fixed',
                percent: 'Percent',
                late_fee_amount: 'Late Fee Amount',
                membership: 'Membership',
                membership_list: 'Membership List',
                property: 'Property',
                property_list: 'Property List',
                package_charges: 'Package & Charges',
                property_list_subtitle: 'Manage property listing data and status',
                package_charges_subtitle: 'Manage plans, owner packages and charges',
                active: 'Active',
                inactive: 'Inactive',
                create: 'Create',
                publish: 'Publish',
                stop: 'Stop',
                draft: 'Draft',
                live: 'Live',
                stopped: 'Stopped',
                ref_no: 'Ref No',
                property_type: 'Property Type',
                unit: 'Unit',
                unit_size: 'Unit Size',
                rent: 'Rent',
                summary: 'Summary',
                use_types: 'Use Types',
                property_details: 'Property Details',
                create_property: 'Create Property',
                edit_property: 'Edit Property',
                customer_id: 'Customer ID',
                thana: 'Thana',
                area: 'Area',
                address: 'Address',
                show_address: 'Show Address',
                allow_foreigner: 'Allow Foreigner',
                allow_show_mobile: 'Show Mobile',
                preferred_call_time: 'Preferred Call Time',
                advanced: 'Advanced',
                latlong: 'Lat/Long',
                space_type: 'Space Type',
                other_facilities: 'Other Facilities',
                image_links: 'Image Links',
                image_link_1: 'Image Link 1',
                image_link_2: 'Image Link 2',
                image_link_3: 'Image Link 3',
                image_link_4: 'Image Link 4',
                video_links: 'Video Links',
                video_link_1: 'Video Link 1',
                video_link_2: 'Video Link 2',
                yes: 'Yes',
                no: 'No',
                plans: 'Plans',
                owner_rent_packages: 'Owner Rent Packages',
                owner_space_packages: 'Owner Space Packages',
                all_property_types: 'All Property Types',
                plan_name: 'Plan Name',
                plan_name_bn: 'Plan Name (BN)',
                validity_days: 'Validity Days',
                rate: 'Rate',
                rate_foreigner: 'Foreigner Rate',
                max_browsing_qty: 'Max Browsing',
                max_contact_view: 'Max Contact View',
                can_view_contact_details: 'Can View Contact',
                save_favourite_listings: 'Save Favourite',
                allow_preference_setup: 'Preference Setup',
                get_auto_notification: 'Auto Notification',
                view_location_on_map: 'View Map',
                order: 'Order',
                package_name: 'Package Name',
                range: 'Range',
                range_from: 'Range From',
                range_to: 'Range To',
                validity_from: 'Validity From',
                validity_to: 'Validity To',
                create_plan: 'Create Plan',
                edit_plan: 'Edit Plan',
                create_owner_rent: 'Create Owner Rent Package',
                edit_owner_rent: 'Edit Owner Rent Package',
                create_owner_space: 'Create Owner Space Package',
                edit_owner_space: 'Edit Owner Space Package',
            },
            bn: {
                dashboard: 'ড্যাশবোর্ড',
                welcome_admin: 'ওয়েলকাম অ্যাডমিন',
                logout: 'লগআউট',
                open_page: 'ওপেন পেজ',
                registration: 'রেজিস্ট্রেশন',
                registration_queue: 'নতুন রেজিস্ট্রেশন',
                registration_queue_subtitle: 'মেম্বারশিপ রেজিস্ট্রেশন অনুমোদন/রিভিউ করুন',
                billing: 'বিলিং',
                settings: 'সেটিংস',
                settings_subtitle: 'সিস্টেম কনফিগারেশন ম্যানেজ করুন',
                language: 'ভাষা',
                language_subtitle: 'অ্যাডমিন প্যানেলের ভাষা পরিবর্তন করুন (ENG / বাংলা)',
                switch_to_light_mode: 'লাইট মোডে যান',
                switch_to_dark_mode: 'ডার্ক মোডে যান',
                configure: 'কনফিগার',
                save: 'সেভ',
                reset: 'রিসেট',
                reload: 'রিলোড',
                save_all: 'সব সেভ',
                settings_saved: 'সেটিংস সেভ হয়েছে।',
                loading: 'লোড হচ্ছে...',
                no_settings_section_found: 'কোন সেটিংস সেকশন পাওয়া যায়নি।',
                customer_payment: 'কাস্টমার পেমেন্ট',
                customer_payment_subtitle: 'কাস্টমার পেমেন্ট অনুমোদন/রিভিউ করুন',
                members: 'মেম্বার',
                members_subtitle: 'মেম্বার লিস্ট, স্ট্যাটাস ও ডিটেইলস ম্যানেজ করুন',
                pending: 'পেন্ডিং',
                processed: 'প্রসেসড',
                confirmed: 'কনফার্মড',
                all: 'সব',
                refresh: 'রিফ্রেশ',
                per_page: 'প্রতি পেজ',
                page: 'পেজ',
                last_updated: 'শেষ আপডেট',
                confirm_payment: 'পেমেন্ট কনফার্ম',
                confirm_payment_hint: 'কনফার্ম করার আগে পেমেন্ট তথ্য যাচাই করুন।',
                search: 'সার্চ',
                total: 'মোট',
                date: 'তারিখ',
                member_type: 'মেম্বার টাইপ',
                corporate: 'কর্পোরেট',
                personal: 'ব্যক্তিগত',
                category: 'ক্যাটাগরি',
                owner: 'মালিক',
                seeker: 'সিকার',
                customer_name: 'কাস্টমার নাম',
                customer: 'কাস্টমার',
                organization_name: 'প্রতিষ্ঠানের নাম',
                mobile: 'মোবাইল',
                email: 'ইমেইল',
                district: 'জেলা',
                status: 'স্ট্যাটাস',
                actions: 'অ্যাকশন',
                accept: 'অ্যাপ্রুভ',
                confirm: 'কনফার্ম',
                add_member: 'মেম্বার যোগ করুন',
                edit_member: 'মেম্বার এডিট',
                member_details: 'মেম্বার ডিটেইলস',
                view: 'ভিউ',
                edit: 'এডিট',
                delete: 'ডিলিট',
                activate: 'অ্যাক্টিভ',
                deactivate: 'ইনঅ্যাক্টিভ',
                close: 'বন্ধ',
                cancel: 'বাতিল',
                previous: 'পূর্ববর্তী',
                next: 'পরবর্তী',
                first: 'প্রথম',
                last: 'শেষ',
                from_date: 'শুরু',
                to_date: 'শেষ',
                clear_filters: 'ক্লিয়ার',
                showing_entries: 'দেখাচ্ছে {from} থেকে {to} / মোট {total}',
                filtered: '(ফিল্টার: {count})',
                confirm_delete: 'আপনি কি নিশ্চিত ডিলিট করবেন?',
                failed: 'ব্যর্থ হয়েছে',
                failed_to_load: 'লোড করা যায়নি',
                failed_to_load_settings: 'সেটিংস লোড করা যায়নি।',
                failed_to_save_settings: 'সেটিংস সেভ করা যায়নি।',
                validation_failed: 'ভ্যালিডেশন ব্যর্থ হয়েছে।',
                charge_calculation_failed: 'চার্জ ক্যালকুলেশন ব্যর্থ হয়েছে',
                fill_required_for_charge_calc: 'চার্জ ক্যালকুলেট করতে প্রপার্টি টাইপ, ইউনিট, ইউনিট সাইজ এবং ভাড়া দিন।',
                optional: 'অপশনাল',
                password: 'পাসওয়ার্ড',
                select: 'সিলেক্ট',
                residence: 'বাসস্থান',
                inside_city: 'সিটির ভিতরে',
                outside_city: 'সিটির বাইরে',
                nationality: 'জাতীয়তা',
                bangladeshi: 'বাংলাদেশি',
                others: 'অন্যান্য',
                country: 'দেশ',
                bill_no: 'বিল নং',
                bill_type: 'বিল টাইপ',
                amount: 'অ্যামাউন্ট',
                payment_date: 'পেমেন্ট তারিখ',
                payment_method: 'পেমেন্ট মেথড',
                trx: 'ট্রানজেকশন',
                no_data: 'কোন ডাটা পাওয়া যায়নি',
                success: 'সফল',
                users_management: 'ইউজার ম্যানেজমেন্ট',
                users_management_subtitle: 'সব সিস্টেম ইউজার ম্যানেজ করুন',
                user_list: 'ইউজার লিস্ট',
                roles: 'রোল',
                permissions: 'পারমিশন',
                rent_seeker: 'রেন্ট সিকার',
                active_package: 'অ্যাক্টিভ প্যাকেজ',
                active_package_subtitle: 'রেন্ট সিকার অ্যাক্টিভ প্ল্যান ম্যানেজ করুন (CRUD)',
                shortlist: 'শর্টলিস্ট',
                shortlist_subtitle: 'সেভড লিস্ট / শর্ট লিস্ট ম্যানেজ করুন (CRUD)',
                add_new_user: 'নতুন ইউজার যোগ করুন',
                search_users: 'ইউজার সার্চ',
                login_id: 'লগইন আইডি',
                user_type: 'ইউজার টাইপ',
                mobile_no: 'মোবাইল নাম্বার',
                pass_reset: 'পাস রিসেট',
                last_login: 'শেষ লগইন',
                created: 'তৈরির তারিখ',
                updated: 'আপডেট তারিখ',
                no_role: 'কোন রোল নেই',
                view_only: 'শুধু ভিউ',
                create_new_user: 'নতুন ইউজার তৈরি',
                edit_user: 'ইউজার এডিট',
                is_pass_reset_required: 'পাস রিসেট লাগবে কি না',
                enter_new_password: 'নতুন পাসওয়ার্ড দিন',
                confirm_password: 'কনফার্ম পাসওয়ার্ড',
                confirm_password_placeholder: 'পাসওয়ার্ড কনফার্ম করুন',
                password_leave_blank: 'খালি রাখলে আগের পাসওয়ার্ড থাকবে',
                last_login_date: 'শেষ লগইন তারিখ',
                create_date: 'তৈরির তারিখ',
                update_date: 'আপডেট তারিখ',
                roles_page_subtitle: 'রোল ম্যানেজ করুন এবং পারমিশন অ্যাসাইন করুন',
                create_role: 'রোল তৈরি করুন',
                search_roles: 'রোল সার্চ',
                slug: 'স্লাগ',
                users: 'ইউজার',
                permissions_count: 'পারমিশন',
                permissions_page_subtitle: 'পারমিশন ম্যানেজ করুন (create/edit/delete)',
                create_permission: 'পারমিশন তৈরি করুন',
                search_permissions: 'পারমিশন সার্চ',
                group: 'গ্রুপ',
                description: 'বর্ণনা',
                general: 'জেনারেল',
                auto_generate_if_empty: 'খালি থাকলে অটো হবে',
                eg_users_management: 'যেমন: ইউজার ম্যানেজমেন্ট',
                optional_placeholder: 'অপশনাল',
                pending_confirmed: 'পেন্ডিং / কনফার্মড',
                name: 'নাম',
                select_visible: 'ভিজিবল সিলেক্ট',
                clear: 'ক্লিয়ার',
                no_permissions_found: 'কোন পারমিশন পাওয়া যায়নি।',
                select_at_least_one_permission: 'অন্তত একটি পারমিশন সিলেক্ট করুন।',
                role_saved_permission_update_failed: 'রোল সেভ হয়েছে, কিন্তু পারমিশন আপডেট হয়নি (role id পাওয়া যায়নি)।',
                role_saved_success: 'রোল সফলভাবে সেভ হয়েছে!',
                failed_to_save_role: 'রোল সেভ করতে ব্যর্থ।',
                role_deleted: 'রোল ডিলিট হয়েছে।',
                failed_to_delete_role: 'রোল ডিলিট করতে ব্যর্থ।',
                permission_saved_success: 'পারমিশন সফলভাবে সেভ হয়েছে!',
                failed_to_save_permission: 'পারমিশন সেভ করতে ব্যর্থ।',
                permission_deleted: 'পারমিশন ডিলিট হয়েছে।',
                failed_to_delete_permission: 'পারমিশন ডিলিট করতে ব্যর্থ।',
                publicity_start: 'পাবলিসিটি শুরু',
                publicity_end: 'পাবলিসিটি শেষ',
                available_from: 'কবে থেকে পাওয়া যাবে',
                publicity_start_date: 'পাবলিসিটি শুরুর তারিখ',
                publicity_end_date: 'পাবলিসিটি শেষের তারিখ',
                property_available_from: 'প্রপার্টি কবে থেকে পাওয়া যাবে',
                floor_type_placeholder: 'নরমাল / টাইলস / মোজাইক',
                floor_level_placeholder: 'যেমন: ৫ম',
                info: 'ইনফো',
                location: 'লোকেশন',
                media: 'মিডিয়া',
                bill: 'বিল',
                id: 'আইডি',
                late_payment: 'লেট পেমেন্ট',
                allowed: 'অনুমোদিত',
                due: 'ডিউ',
                fee: 'ফি',
                features: 'ফিচারস',
                preferences: 'পছন্দসমূহ',
                rooms_spaces: 'রুম ও স্পেস',
                total_room: 'মোট রুম',
                balconies: 'বারান্দা',
                dining_room: 'ডাইনিং রুম',
                dining_room_size: 'ডাইনিং রুম সাইজ',
                drawing_room: 'ড্রয়িং রুম',
                drawing_room_size: 'ড্রয়িং রুম সাইজ',
                attached_dining_drawing: 'এটাচড ডাইনিং ও ড্রয়িং',
                attached_dining_drawing_size: 'এটাচড ডাইনিং ও ড্রয়িং সাইজ',
                floor_type: 'ফ্লোর টাইপ',
                floor_level: 'ফ্লোর লেভেল',
                total_floor_in_building: 'বিল্ডিংয়ে মোট ফ্লোর',
                kitchen_features: 'কিচেন ফিচারস',
                has_kitchen: 'কিচেন আছে',
                kitchen_description: 'কিচেন ডেসক্রিপশন',
                gas_connection_type: 'গ্যাস কানেকশন টাইপ',
                bathroom_features: 'বাথরুম ফিচারস',
                attached_bath: 'এটাচড বাথ',
                common_bath: 'কমন বাথ',
                high_commode: 'হাই কমোড',
                low_commode: 'লো কমোড',
                utility_services: 'ইউটিলিটি ও সার্ভিস',
                electricity: 'বিদ্যুৎ',
                dedicated: 'ডেডিকেটেড',
                shared: 'শেয়ার্ড',
                water_wasa: 'পানি (ওয়াসা)',
                water_deep_tubewell: 'পানি (ডিপ টিউবওয়েল)',
                lift: 'লিফট',
                parking_space: 'পার্কিং স্পেস',
                community_space: 'কমিউনিটি স্পেস',
                generator_backup_power: 'জেনারেটর/ব্যাকআপ',
                internet_broadband: 'ইন্টারনেট / ব্রডব্যান্ড',
                security_safety: 'সিকিউরিটি ও সেফটি',
                security_guard: 'সিকিউরিটি গার্ড',
                cctv_surveillance: 'সিসিটিভি',
                intercom_system: 'ইন্টারকম',
                fire_safety_system: 'ফায়ার সেফটি',
                furnishing_status: 'ফার্নিশিং স্ট্যাটাস',
                unfurnished: 'আনফার্নিশড',
                furnished: 'ফার্নিশড',
                furnished_details: 'ফার্নিশড ডিটেইলস',
                residential_preference: 'রেসিডেন্সিয়াল প্রেফারেন্স',
                family: 'ফ্যামিলি',
                small_family: 'ছোট ফ্যামিলি',
                bachelor: 'ব্যাচেলর',
                student_male: 'স্টুডেন্ট (ছেলে)',
                student_female: 'স্টুডেন্ট (মেয়ে)',
                service_holder: 'চাকরিজীবী',
                any: 'যে কেউ',
                attachment_rate_list: 'অ্যাটাচমেন্ট রেট লিস্ট',
                no_active_attachment_rate: 'কোন অ্যাক্টিভ অ্যাটাচমেন্ট রেট নেই।',
                public_view_status: 'পাবলিক ভিউ স্ট্যাটাস',
                visible: 'ভিজিবল',
                hidden: 'হিডেন',
                bill_id: 'বিল আইডি',
                bill_status: 'বিল স্ট্যাটাস',
                plan_id: 'প্ল্যান আইডি',
                plan_start_date: 'প্ল্যান শুরুর তারিখ',
                validity_date: 'মেয়াদ শেষের তারিখ',
                customer_active_plan_id: 'কাস্টমার অ্যাক্টিভ প্ল্যান আইডি',
                to_let_notice_id: 'টু-লেট নোটিশ আইডি',
                list_type: 'লিস্ট টাইপ',
                saved: 'সেভড',
                short: 'শর্ট',
                make_saved: 'সেভড করুন',
                make_short: 'শর্ট করুন',
                saving: 'সেভ হচ্ছে...',
                estimated_publish_charge: 'আনুমানিক পাবলিশ চার্জ',
                calculate: 'ক্যালকুলেট',
                calculating: 'ক্যালকুলেট হচ্ছে...',
                space: 'স্পেস',
                attachments: 'অ্যাটাচমেন্ট',
                set_unit_unit_size_rent_then_calculate: 'Unit, Unit Size এবং Rent সেট করে ক্যালকুলেট চাপুন।',
                late_payment_allowed: 'লেট পেমেন্ট অনুমোদিত',
                payment_due_date: 'পেমেন্ট ডিউ তারিখ',
                late_fee_type: 'লেট ফি টাইপ',
                fixed: 'ফিক্সড',
                percent: 'পারসেন্ট',
                late_fee_amount: 'লেট ফি পরিমাণ',
                membership: 'মেম্বারশিপ',
                membership_list: 'মেম্বারশিপ লিস্ট',
                property: 'প্রপার্টি',
                property_list: 'প্রপার্টি লিস্ট',
                package_charges: 'প্যাকেজ ও চার্জ',
                property_list_subtitle: 'প্রপার্টি লিস্ট ও স্ট্যাটাস ম্যানেজ করুন',
                package_charges_subtitle: 'প্ল্যান, ওনার প্যাকেজ এবং চার্জ ম্যানেজ করুন',
                active: 'অ্যাক্টিভ',
                inactive: 'ইনঅ্যাক্টিভ',
                create: 'নতুন',
                publish: 'লাইভ',
                stop: 'স্টপ',
                draft: 'ড্রাফট',
                live: 'লাইভ',
                stopped: 'স্টপড',
                ref_no: 'রেফ নং',
                property_type: 'প্রপার্টি টাইপ',
                unit: 'ইউনিট',
                unit_size: 'ইউনিট সাইজ',
                rent: 'ভাড়া',
                summary: 'সারাংশ',
                use_types: 'ইউজ টাইপ',
                property_details: 'প্রপার্টি বিস্তারিত',
                create_property: 'প্রপার্টি যোগ করুন',
                edit_property: 'প্রপার্টি এডিট',
                customer_id: 'কাস্টমার আইডি',
                thana: 'থানা',
                area: 'এলাকা',
                address: 'ঠিকানা',
                show_address: 'ঠিকানা দেখাবে',
                allow_foreigner: 'ফরেনার এলাউ',
                allow_show_mobile: 'মোবাইল দেখাবে',
                preferred_call_time: 'পছন্দের কল টাইম',
                advanced: 'অ্যাডভান্সড',
                latlong: 'ল্যাট/লং',
                space_type: 'স্পেস টাইপ',
                other_facilities: 'অন্যান্য সুবিধা',
                image_links: 'ইমেজ লিংক',
                image_link_1: 'ইমেজ লিংক ১',
                image_link_2: 'ইমেজ লিংক ২',
                image_link_3: 'ইমেজ লিংক ৩',
                image_link_4: 'ইমেজ লিংক ৪',
                video_links: 'ভিডিও লিংক',
                video_link_1: 'ভিডিও লিংক ১',
                video_link_2: 'ভিডিও লিংক ২',
                yes: 'হ্যাঁ',
                no: 'না',
                plans: 'প্ল্যান',
                owner_rent_packages: 'ওনার রেন্ট প্যাকেজ',
                owner_space_packages: 'ওনার স্পেস প্যাকেজ',
                all_property_types: 'সব প্রপার্টি টাইপ',
                plan_name: 'প্ল্যান নাম',
                plan_name_bn: 'প্ল্যান নাম (বাংলা)',
                validity_days: 'মেয়াদ (দিন)',
                rate: 'রেট',
                rate_foreigner: 'ফরেনার রেট',
                max_browsing_qty: 'সর্বোচ্চ ব্রাউজিং',
                max_contact_view: 'সর্বোচ্চ কন্টাক্ট ভিউ',
                can_view_contact_details: 'কন্টাক্ট দেখবে',
                save_favourite_listings: 'ফেভারিট সেভ',
                allow_preference_setup: 'প্রেফারেন্স সেটআপ',
                get_auto_notification: 'অটো নোটিফিকেশন',
                view_location_on_map: 'ম্যাপে দেখবে',
                order: 'অর্ডার',
                package_name: 'প্যাকেজ নাম',
                range: 'রেঞ্জ',
                range_from: 'শুরু',
                range_to: 'শেষ',
                validity_from: 'শুরুর তারিখ',
                validity_to: 'শেষের তারিখ',
                create_plan: 'প্ল্যান যোগ করুন',
                edit_plan: 'প্ল্যান এডিট',
                create_owner_rent: 'রেন্ট প্যাকেজ যোগ করুন',
                edit_owner_rent: 'রেন্ট প্যাকেজ এডিট',
                create_owner_space: 'স্পেস প্যাকেজ যোগ করুন',
                edit_owner_space: 'স্পেস প্যাকেজ এডিট',
            },
        };

        const adminT = (key) => {
            const l = lang.value === 'bn' ? 'bn' : 'en';
            return translations[l]?.[key] || translations.en[key] || key;
        };

        const setLang = (value) => {
            lang.value = value === 'bn' ? 'bn' : 'en';
            localStorage.setItem('admin_lang', lang.value);
        };

        provide('adminT', adminT);
        provide('adminLang', lang);
        provide('adminSetLang', setLang);

        const applyTheme = () => {
            if (typeof document === 'undefined') return;
            const v = theme.value === 'dark' ? 'dark' : 'light';
            theme.value = v;
            document.documentElement.setAttribute('data-admin-theme', v);
        };

        const toggleTheme = () => {
            theme.value = theme.value === 'dark' ? 'light' : 'dark';
            localStorage.setItem('admin_theme', theme.value);
            applyTheme();
        };

        const updateCurrentRoute = () => {
            const path = window.location.pathname;
            if (path.includes('/admin/dashboard')) {
                currentRouteGroup.value = 'dashboard';
                currentRoute.value = 'dashboard';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = false;
                return;
            }
            if (path.includes('/admin/settings')) {
                currentRouteGroup.value = 'settings';
                currentRoute.value = 'settings';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = false;
                return;
            }

            if (path.includes('/admin/registration-queue')) {
                currentRouteGroup.value = 'registration';
                currentRoute.value = 'registration-queue';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = true;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = false;
                return;
            }

            if (path.includes('/admin/customers')) {
                currentRouteGroup.value = 'membership';
                currentRoute.value = 'customers';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = true;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = false;
                return;
            }

            if (path.includes('/admin/package-charges')) {
                currentRouteGroup.value = 'property';
                currentRoute.value = 'package-charges';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = true;
                seekerMenuOpen.value = false;
                return;
            }

            if (path.includes('/admin/properties') || path.includes('/admin/tolet-notices')) {
                currentRouteGroup.value = 'property';
                currentRoute.value = 'properties';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = true;
                seekerMenuOpen.value = false;
                return;
            }

            if (path.includes('/admin/rent-seeker/active-package')) {
                currentRouteGroup.value = 'seeker';
                currentRoute.value = 'active-package';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = true;
                return;
            }

            if (path.includes('/admin/rent-seeker/shortlist')) {
                currentRouteGroup.value = 'seeker';
                currentRoute.value = 'shortlist';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = true;
                return;
            }

            if (path.includes('/admin/billing')) {
                currentRouteGroup.value = 'billing';
                currentRoute.value = 'billing';
                usersMenuOpen.value = false;
                registrationMenuOpen.value = false;
                membershipMenuOpen.value = false;
                propertyMenuOpen.value = false;
                seekerMenuOpen.value = false;
                return;
            }

            currentRouteGroup.value = 'users';
            usersMenuOpen.value = true;
            registrationMenuOpen.value = false;
            membershipMenuOpen.value = false;
            propertyMenuOpen.value = false;
            seekerMenuOpen.value = false;

            if (path.includes('/admin/roles')) {
                currentRoute.value = 'roles';
                return;
            }

            if (path.includes('/admin/permissions')) {
                currentRoute.value = 'permissions';
                return;
            }

            currentRoute.value = 'users';
        };

        const loadUserPermissions = async () => {
            try {
                const res = await axios.get('/admin/api/user-permissions');
                userPermissions.value = Array.isArray(res.data.permissions) ? res.data.permissions : [];
            } catch (e) {
                userPermissions.value = [];
            } finally {
                permissionsLoaded.value = true;
            }
        };

        const hasAnyPermission = (patterns) => {
            if (!permissionsLoaded.value) return false;
            const perms = userPermissions.value || [];
            if (perms.includes('*')) return true;
            const list = Array.isArray(patterns) ? patterns : [patterns];
            return perms.some(p => list.some(x => (p || '').toLowerCase() === (x || '').toLowerCase()));
        };

        const canShowUsersGroup = computed(() => hasAnyPermission([
            'view-users', 'create-users', 'edit-users', 'edite-users', 'delete-users',
            'view-roles', 'create-roles', 'edit-roles', 'edite-roles', 'delete-roles',
            'view-permissions', 'create-permissions', 'edit-permissions', 'edite-permissions', 'delete-permissions',
        ]));

        const canShowUsersLink = computed(() => hasAnyPermission(['view-users', 'create-users', 'edit-users', 'edite-users', 'delete-users']));
        const canShowRolesLink = computed(() => hasAnyPermission(['view-roles', 'create-roles', 'edit-roles', 'edite-roles', 'delete-roles']));
        const canShowPermissionsLink = computed(() => hasAnyPermission(['view-permissions', 'create-permissions', 'edit-permissions', 'edite-permissions', 'delete-permissions']));
        const canShowRegistrationGroup = computed(() => hasAnyPermission(['view-registrations', 'approve-registrations', 'edit-registrations', 'delete-registrations']));
        const canShowRegistrationQueueLink = computed(() => hasAnyPermission(['view-registrations', 'approve-registrations']));
        const canShowMembershipGroup = computed(() => hasAnyPermission(['view-customers', 'create-customers', 'edit-customers', 'delete-customers', 'toggle-customers']));
        const canShowMembershipListLink = computed(() => hasAnyPermission(['view-customers', 'create-customers', 'edit-customers', 'delete-customers', 'toggle-customers']));
        const canShowPropertyGroup = computed(() => hasAnyPermission([
            'property-view', 'property-create', 'property-edit', 'property-delete', 'property-toggle',
            'package-view', 'package-create', 'package-edit', 'package-delete', 'package-toggle',
            'owner-tolet-view', 'owner-tolet-create', 'owner-tolet-edit', 'owner-tolet-publish', 'owner-tolet-stop',
        ]));
        const canShowPropertyListLink = computed(() => hasAnyPermission([
            'property-view', 'property-create', 'property-edit', 'property-delete', 'property-toggle',
            'owner-tolet-view', 'owner-tolet-create', 'owner-tolet-edit', 'owner-tolet-publish', 'owner-tolet-stop',
        ]));
        const canShowPackageChargesLink = computed(() => hasAnyPermission(['package-view', 'package-create', 'package-edit', 'package-delete', 'package-toggle']));
        const canShowSeekerGroup = computed(() => hasAnyPermission(['seeker-active-package', 'seeker-shortlist-view', 'seeker-shortlist-manage']));
        const canShowActivePackageLink = computed(() => hasAnyPermission(['seeker-active-package']));
        const canShowShortlistLink = computed(() => hasAnyPermission(['seeker-shortlist-view', 'seeker-shortlist-manage']));
        const canShowBilling = computed(() => hasAnyPermission(['view-bill', 'submit-payment']));
        const canShowSettings = computed(() => hasAnyPermission(['view-settings', 'edit-settings']));
        const canShowDashboard = computed(() => hasAnyPermission([
            'view-users', 'create-users', 'edit-users', 'edite-users', 'delete-users',
            'view-roles', 'create-roles', 'edit-roles', 'edite-roles', 'delete-roles',
            'view-permissions', 'create-permissions', 'edit-permissions', 'edite-permissions', 'delete-permissions',
            'view-registrations', 'approve-registrations', 'edit-registrations', 'delete-registrations',
            'view-customers', 'create-customers', 'edit-customers', 'delete-customers', 'toggle-customers',
            'property-view', 'property-create', 'property-edit', 'property-delete', 'property-toggle',
            'package-view', 'package-create', 'package-edit', 'package-delete', 'package-toggle',
            'owner-tolet-view', 'owner-tolet-create', 'owner-tolet-edit', 'owner-tolet-publish', 'owner-tolet-stop',
            'seeker-active-package', 'seeker-shortlist-view', 'seeker-shortlist-manage',
            'view-bill', 'submit-payment',
            'view-settings', 'edit-settings',
        ]));

        onMounted(async () => {
            applyTheme();
            await loadUserPermissions();
            updateCurrentRoute();
        });

        const userInitials = computed(() => {
            const name = userName.value || 'A';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        });

        const pageTitle = computed(() => {
            if (currentRouteGroup.value === 'dashboard') return adminT('dashboard');
            if (currentRouteGroup.value === 'settings') return adminT('settings');
            if (currentRouteGroup.value === 'registration') return adminT('registration_queue');
            if (currentRouteGroup.value === 'membership') return adminT('membership_list');
            if (currentRouteGroup.value === 'property' && currentRoute.value === 'package-charges') return adminT('package_charges');
            if (currentRouteGroup.value === 'property') return adminT('property_list');
            if (currentRouteGroup.value === 'seeker' && currentRoute.value === 'active-package') return adminT('active_package');
            if (currentRouteGroup.value === 'seeker' && currentRoute.value === 'shortlist') return adminT('shortlist');
            if (currentRouteGroup.value === 'billing') return adminT('billing');
            if (currentRoute.value === 'roles') return adminT('roles');
            if (currentRoute.value === 'permissions') return adminT('permissions');
            return adminT('users_management');
        });

        const toggleSidebar = () => {
            sidebarCollapsed.value = !sidebarCollapsed.value;
        };

        const toggleUsersMenu = () => {
            usersMenuOpen.value = !usersMenuOpen.value;
        };

        const toggleRegistrationMenu = () => {
            registrationMenuOpen.value = !registrationMenuOpen.value;
        };

        const toggleMembershipMenu = () => {
            membershipMenuOpen.value = !membershipMenuOpen.value;
        };

        const togglePropertyMenu = () => {
            propertyMenuOpen.value = !propertyMenuOpen.value;
        };

        const toggleSeekerMenu = () => {
            seekerMenuOpen.value = !seekerMenuOpen.value;
        };

        const logout = () => {
            document.getElementById('logout-form').submit();
        };

        return {
            sidebarCollapsed,
            currentRoute,
            currentRouteGroup,
            usersMenuOpen,
            registrationMenuOpen,
            membershipMenuOpen,
            propertyMenuOpen,
            seekerMenuOpen,
            pageTitle,
            systemName,
            systemLogo,
            userName,
            userInitials,
            lang,
            setLang,
            theme,
            toggleTheme,
            adminT,
            permissionsLoaded,
            canShowDashboard,
            canShowUsersGroup,
            canShowUsersLink,
            canShowRolesLink,
            canShowPermissionsLink,
            canShowRegistrationGroup,
            canShowRegistrationQueueLink,
            canShowMembershipGroup,
            canShowMembershipListLink,
            canShowPropertyGroup,
            canShowPropertyListLink,
            canShowPackageChargesLink,
            canShowSeekerGroup,
            canShowActivePackageLink,
            canShowShortlistLink,
            canShowBilling,
            canShowSettings,
            toggleSidebar,
            toggleUsersMenu,
            toggleRegistrationMenu,
            toggleMembershipMenu,
            togglePropertyMenu,
            toggleSeekerMenu,
            logout,
        };
    },
};
</script>

<style scoped>
:global(html) {
    --admin-surface: #ffffff;
    --admin-header-bg: #ffffff;
    --admin-text: #0f172a;
    --admin-muted: #64748b;
    --admin-border: #e5e7eb;
    --admin-chip-bg: #f8fafc;
    --admin-chip-border: #e2e8f0;
    --admin-card-bg: #ffffff;
    --admin-card-border: rgba(15, 23, 42, 0.1);
    --admin-soft-bg: #f7fafc;
    --admin-input-bg: #ffffff;
    --admin-input-border: #e2e8f0;
    --admin-row-hover: #f7fafc;
    --admin-pill-bg: rgba(15, 23, 42, 0.04);
    --admin-pill-border: rgba(15, 23, 42, 0.14);
    --admin-pill-active-bg: rgba(15, 23, 42, 0.88);
    --admin-pill-active-text: #ffffff;
    --admin-user-icon-bg: rgba(16, 185, 129, 0.14);
    --admin-user-icon-color: #059669;
    --admin-danger: #ef4444;
    --admin-danger-bg: rgba(239, 68, 68, 0.08);
}

:global(html[data-admin-theme='dark']) {
    --admin-surface: #0b1220;
    --admin-header-bg: #0b1220;
    --admin-text: #e2e8f0;
    --admin-muted: #94a3b8;
    --admin-border: rgba(148, 163, 184, 0.2);
    --admin-chip-bg: rgba(148, 163, 184, 0.12);
    --admin-chip-border: rgba(148, 163, 184, 0.22);
    --admin-card-bg: rgba(15, 23, 42, 0.7);
    --admin-card-border: rgba(148, 163, 184, 0.22);
    --admin-soft-bg: rgba(148, 163, 184, 0.12);
    --admin-input-bg: rgba(15, 23, 42, 0.55);
    --admin-input-border: rgba(148, 163, 184, 0.26);
    --admin-row-hover: rgba(148, 163, 184, 0.08);
    --admin-pill-bg: rgba(148, 163, 184, 0.12);
    --admin-pill-border: rgba(148, 163, 184, 0.22);
    --admin-pill-active-bg: #e2e8f0;
    --admin-pill-active-text: #0b1220;
    --admin-danger: #f87171;
    --admin-danger-bg: rgba(248, 113, 113, 0.12);
}

:global(html[data-admin-theme='dark'] body) {
    background: var(--admin-surface) !important;
    color: var(--admin-text) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .content-area {
    background: var(--admin-surface) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .card,
:global(html[data-admin-theme='dark']) .admin-layout .dashboard-card,
:global(html[data-admin-theme='dark']) .admin-layout .modal-card {
    background: var(--admin-card-bg) !important;
    border: 1px solid var(--admin-card-border) !important;
    box-shadow: none !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .modal-head,
:global(html[data-admin-theme='dark']) .admin-layout .modal-actions {
    border-color: var(--admin-border) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .modal-title,
:global(html[data-admin-theme='dark']) .admin-layout .primary-title,
:global(html[data-admin-theme='dark']) .admin-layout .user-name,
:global(html[data-admin-theme='dark']) .admin-layout .detail-label,
:global(html[data-admin-theme='dark']) .admin-layout .detail-value,
:global(html[data-admin-theme='dark']) .admin-layout .form-label {
    color: var(--admin-text) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .page-subtitle,
:global(html[data-admin-theme='dark']) .admin-layout .primary-sub,
:global(html[data-admin-theme='dark']) .admin-layout .hint,
:global(html[data-admin-theme='dark']) .admin-layout .empty,
:global(html[data-admin-theme='dark']) .admin-layout .data-table th {
    color: var(--admin-muted) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .search-input,
:global(html[data-admin-theme='dark']) .admin-layout .form-input,
:global(html[data-admin-theme='dark']) .admin-layout input,
:global(html[data-admin-theme='dark']) .admin-layout textarea,
:global(html[data-admin-theme='dark']) .admin-layout select {
    background: var(--admin-input-bg) !important;
    border-color: var(--admin-input-border) !important;
    color: var(--admin-text) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .data-table thead {
    background: var(--admin-soft-bg) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .data-table td {
    border-top-color: var(--admin-border) !important;
    color: var(--admin-text) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .data-table tbody tr:hover {
    background: var(--admin-row-hover) !important;
}

:global(html[data-admin-theme='dark']) .admin-layout .tag-gray,
:global(html[data-admin-theme='dark']) .admin-layout .btn-secondary {
    background: var(--admin-soft-bg) !important;
    color: var(--admin-text) !important;
}

.admin-layout {
    display: flex;
    min-height: 100vh;
    background: var(--admin-surface);
}

.sidebar {
    width: 280px;
    background: linear-gradient(180deg, #1a2332 0%, #0f172a 100%);
    display: flex;
    flex-direction: column;
    transition: width 0.3s ease;
    position: fixed;
    height: 100vh;
    z-index: 1000;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.25);
}

.sidebar-collapsed {
    width: 80px;
}

.sidebar-header {
    padding: 32px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.logo {
    display: flex;
    align-items: center;
    gap: 14px;
    color: #ffffff;
}

.logo-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: #ffffff;
    overflow: hidden;
}

.logo-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.logo-text {
    font-size: 18px;
    font-weight: 700;
    white-space: nowrap;
    letter-spacing: -0.5px;
    color: #ffffff;
}

.sidebar-nav {
    padding: 24px 16px;
    flex: 1;
}

.nav-group {
    margin-bottom: 8px;
}

.nav-parent {
    width: 100%;
    justify-content: flex-start;
    background: transparent;
    border: none;
    cursor: pointer;
}

.nav-parent-text {
    flex: 1;
    text-align: left;
}

.nav-chevron {
    opacity: 0.85;
}

.nav-submenu {
    margin: 6px 0 12px 52px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.nav-subitem {
    padding: 10px 12px;
    border-radius: 10px;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.75);
    font-size: 13px;
    font-weight: 600;
    transition: background 0.2s ease, color 0.2s ease;
}

.nav-subitem:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
}

.nav-subitem.active {
    background: rgba(59, 130, 246, 0.18);
    color: #ffffff;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    margin-bottom: 8px;
    border-radius: 12px;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.8);
    transition: background 0.2s ease, color 0.2s ease;
}

.nav-item:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
}

.nav-item.active {
    background: rgba(59, 130, 246, 0.18);
    color: #ffffff;
}

.sidebar-footer {
    padding: 18px 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    justify-content: flex-end;
}

.sidebar-toggle {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: #ffffff;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.main-content {
    flex: 1;
    margin-left: 280px;
    min-height: 100vh;
    transition: margin-left 0.3s ease;
}

.sidebar-collapsed + .main-content {
    margin-left: 80px;
}

.top-header {
    height: 76px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--admin-border);
    background: var(--admin-header-bg);
    position: sticky;
    top: 0;
    z-index: 900;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
}

.page-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
    color: var(--admin-text);
}

.lang-pill {
    display: flex;
    gap: 6px;
    padding: 4px;
    border-radius: 999px;
    border: 1px solid var(--admin-pill-border);
    background: var(--admin-pill-bg);
}

.lang-pill-btn {
    border: none;
    background: transparent;
    color: var(--admin-text);
    padding: 8px 14px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
}

.lang-pill-btn.active {
    background: var(--admin-pill-active-bg);
    color: var(--admin-pill-active-text);
}

.theme-btn {
    border: 1px solid var(--admin-pill-border);
    background: var(--admin-chip-bg);
    color: var(--admin-text);
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.user-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid var(--admin-pill-border);
    background: var(--admin-chip-bg);
    border-radius: 999px;
    padding: 6px 14px 6px 8px;
    color: var(--admin-text);
    font-weight: 700;
    font-size: 13px;
}

.user-pill-icon {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--admin-user-icon-bg);
    color: var(--admin-user-icon-color);
}

.user-pill-name {
    white-space: nowrap;
}

.logout-btn {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 1px solid var(--admin-chip-border);
    background: var(--admin-chip-bg);
    color: var(--admin-text);
}

.logout-btn.logout-danger {
    border-color: rgba(239, 68, 68, 0.4);
    background: var(--admin-danger-bg);
    color: var(--admin-danger);
}

.content-area {
    padding: 24px;
}

.lang-toggle {
    display: none;
}
</style>
